import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { WarehouseLayout } from '@components/layout/WarehouseLayout';
import { EquipmentAssetsTable } from '@components/organisms/EquipmentAssetsTable';
import { EquipmentAssetFormModal } from '@components/organisms/EquipmentAssetFormModal';
import { EquipmentCheckoutModal } from '@components/organisms/EquipmentCheckoutModal';
import { EquipmentReturnModal } from '@components/organisms/EquipmentReturnModal';
import { EquipmentMaintenanceModal } from '@components/organisms/EquipmentMaintenanceModal';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { CreateEditTaskModal } from '@components/CreateEditTaskModal';
import {
  useEquipmentAssets,
  useMaterialCategoriesByType,
  useWarehouses,
  useCreateEquipmentAsset,
  useUpdateEquipmentAsset,
  useCheckoutEquipment,
  useReturnEquipment,
  useSetEquipmentMaintenance,
  useDeleteEquipmentAsset,
} from '@hooks/api/warehouse';
import { useTask } from '@hooks/api';
import {
  CategoryType,
  EquipmentAsset,
  EquipmentAssetCreateInput,
  EquipmentAssetUpdateInput,
  EquipmentCheckoutInput,
  EquipmentReturnInput,
  EquipmentMaintenanceInput,
} from '@models/warehouse.model';
import { generateEquipmentAssetsPdf } from '@utils/equipmentAssetsPdf';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';

const EquipmentAssetsPage: React.FC = () => {
  const tActionMessages = useTranslation('actionMessages');
  const history = useHistory();

  // Modal states
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<EquipmentAsset | null>(null);
  const [equipmentToCheckout, setEquipmentToCheckout] = useState<EquipmentAsset | null>(null);
  const [equipmentToReturn, setEquipmentToReturn] = useState<EquipmentAsset | null>(null);
  const [equipmentToDelete, setEquipmentToDelete] = useState<EquipmentAsset | null>(null);
  const [equipmentToMaintenance, setEquipmentToMaintenance] = useState<EquipmentAsset | null>(null);

  // API Hooks
  const {
    data: equipment = [],
    isLoading: equipmentLoading,
    isError: equipmentError,
    error: equipmentErrorObj,
    refetch: refetchEquipment,
  } = useEquipmentAssets();

  const {
    data: categories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useMaterialCategoriesByType(CategoryType.EQUIPMENT);

  const {
    data: warehouses = [],
  } = useWarehouses();

  const { task, fetchTask, setTask } = useTask();

  // Mutations
  const createMutation = useCreateEquipmentAsset();
  const updateMutation = useUpdateEquipmentAsset();
  const checkoutMutation = useCheckoutEquipment();
  const returnMutation = useReturnEquipment();
  const maintenanceMutation = useSetEquipmentMaintenance();
  const deleteMutation = useDeleteEquipmentAsset();

  // Consider errors as "done loading" to not block the UI
  const isLoading = (equipmentLoading && !equipmentError) || (categoriesLoading && !categoriesError);

  // Handlers
  const handleRefresh = useCallback(() => {
    refetchEquipment();
  }, [refetchEquipment]);

  const handleAddEquipment = useCallback(() => {
    setSelectedEquipment(null);
    setIsFormModalOpen(true);
  }, []);

  const handleEdit = useCallback((asset: EquipmentAsset) => {
    setSelectedEquipment(asset);
    setIsFormModalOpen(true);
  }, []);

  const handleCloseFormModal = useCallback(() => {
    setIsFormModalOpen(false);
    setSelectedEquipment(null);
  }, []);

  const handleSubmitForm = useCallback(
    async (data: EquipmentAssetCreateInput | EquipmentAssetUpdateInput) => {
      try {
        if ('assetId' in data && data.assetId) {
          await updateMutation.mutateAsync(data as EquipmentAssetUpdateInput);
          toast.success(tActionMessages.equipmentUpdatedSuccessfully);
        } else {
          await createMutation.mutateAsync(data as EquipmentAssetCreateInput);
          toast.success(tActionMessages.equipmentCreatedSuccessfully);
        }
        handleCloseFormModal();
      } catch (error: any) {
        console.error('Error saving equipment:', error);
        const errorMessage = error?.response?.errors?.[0]?.message || error?.message || '';
        if (errorMessage.includes('unique_asset_code_org') || errorMessage.includes('duplicate key')) {
          toast.error(tActionMessages.equipmentAssetCodeExists);
        } else if (errorMessage.includes('unique') || errorMessage.includes('already exists')) {
          toast.error(tActionMessages.equipmentNameExists);
        } else {
          toast.error(tActionMessages.failedToSaveEquipment);
        }
      }
    },
    [
      createMutation,
      updateMutation,
      handleCloseFormModal,
      tActionMessages.equipmentUpdatedSuccessfully,
      tActionMessages.equipmentCreatedSuccessfully,
      tActionMessages.equipmentAssetCodeExists,
      tActionMessages.equipmentNameExists,
      tActionMessages.failedToSaveEquipment,
    ]
  );

  const handleCheckout = useCallback((asset: EquipmentAsset) => {
    setEquipmentToCheckout(asset);
    setIsCheckoutModalOpen(true);
  }, []);

  const handleCloseCheckoutModal = useCallback(() => {
    setIsCheckoutModalOpen(false);
    setEquipmentToCheckout(null);
  }, []);

  const handleSubmitCheckout = useCallback(
    async (input: EquipmentCheckoutInput) => {
      try {
        await checkoutMutation.mutateAsync(input);
        toast.success(tActionMessages.equipmentCheckedOutSuccessfully);
        handleCloseCheckoutModal();
      } catch (error) {
        console.error('Error checking out equipment:', error);
        toast.error(tActionMessages.failedToCheckoutEquipment);
      }
    },
    [
      checkoutMutation,
      handleCloseCheckoutModal,
      tActionMessages.equipmentCheckedOutSuccessfully,
      tActionMessages.failedToCheckoutEquipment,
    ]
  );

  const handleReturn = useCallback((asset: EquipmentAsset) => {
    setEquipmentToReturn(asset);
    setIsReturnModalOpen(true);
  }, []);

  const handleCloseReturnModal = useCallback(() => {
    setIsReturnModalOpen(false);
    setEquipmentToReturn(null);
  }, []);

  const handleSubmitReturn = useCallback(
    async (input: EquipmentReturnInput) => {
      try {
        await returnMutation.mutateAsync(input);
        toast.success(tActionMessages.equipmentReturnedSuccessfully);
        handleCloseReturnModal();
      } catch (error) {
        console.error('Error returning equipment:', error);
        toast.error(tActionMessages.failedToReturnEquipment);
      }
    },
    [
      returnMutation,
      handleCloseReturnModal,
      tActionMessages.equipmentReturnedSuccessfully,
      tActionMessages.failedToReturnEquipment,
    ]
  );

  const handleMaintenance = useCallback((asset: EquipmentAsset) => {
    setEquipmentToMaintenance(asset);
    setIsMaintenanceModalOpen(true);
  }, []);

  const handleCloseMaintenanceModal = useCallback(() => {
    setIsMaintenanceModalOpen(false);
    setEquipmentToMaintenance(null);
  }, []);

  const handleSubmitMaintenance = useCallback(
    async (input: EquipmentMaintenanceInput) => {
      try {
        await maintenanceMutation.mutateAsync(input);
        toast.success(tActionMessages.equipmentSetToMaintenance);
        handleCloseMaintenanceModal();
      } catch (error) {
        console.error('Error setting maintenance:', error);
        toast.error(tActionMessages.failedToSetMaintenanceStatus);
      }
    },
    [
      maintenanceMutation,
      handleCloseMaintenanceModal,
      tActionMessages.equipmentSetToMaintenance,
      tActionMessages.failedToSetMaintenanceStatus,
    ]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteEquipment = useCallback((asset: EquipmentAsset) => {
    setEquipmentToDelete(asset);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!equipmentToDelete) return;

    try {
      await deleteMutation.mutateAsync(equipmentToDelete.assetId);
      toast.success(tActionMessages.equipmentDeletedSuccessfully);
      setEquipmentToDelete(null);
    } catch (error) {
      console.error('Error deleting equipment:', error);
      toast.error(tActionMessages.failedToDeleteEquipment);
    }
  }, [
    equipmentToDelete,
    deleteMutation,
    tActionMessages.equipmentDeletedSuccessfully,
    tActionMessages.failedToDeleteEquipment,
  ]);

  const handleCancelDelete = useCallback(() => {
    setEquipmentToDelete(null);
  }, []);

  const handleViewDetail = useCallback((asset: EquipmentAsset) => {
    history.push(`/warehouse/equipment/${asset.assetId}`);
  }, [history]);

  const handleViewTask = useCallback((taskId: string) => {
    fetchTask(taskId);
    setIsTaskModalOpen(true);
  }, [fetchTask]);

  const handleExport = useCallback(() => {
    try {
      generateEquipmentAssetsPdf({
        equipment,
        categories,
      });
      toast.success(tActionMessages.equipmentAssetsExportedSuccessfully);
    } catch (error) {
      console.error('Error exporting equipment assets:', error);
      toast.error(tActionMessages.failedToExportEquipmentAssets);
    }
  }, [
    equipment,
    categories,
    tActionMessages.equipmentAssetsExportedSuccessfully,
    tActionMessages.failedToExportEquipmentAssets,
  ]);

  if (equipmentLoading && !equipmentError) {
    return (
      <WarehouseLayout>
        <div className="flex justify-center items-center h-full">
          <Loading size="xl" />
        </div>
      </WarehouseLayout>
    );
  }

  return (
    <WarehouseLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Equipment Assets
            </h1>
            <p className="text-sm text-gray-500">
              Manage your organization's equipment inventory
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="secondary"
              leftIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
              onClick={handleExport}
            >
              Export PDF
            </Button>
            <Button
              variant="primary"
              leftIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={handleAddEquipment}
            >
              Add Equipment
            </Button>
            <Button variant="tertiary" onClick={handleRefresh}>
              <RefreshIcon sx={{ fontSize: 16 }} />
            </Button>
          </div>
        </div>

        {/* Error Banner */}
        {equipmentError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <ErrorOutlineIcon className="text-red-500 mt-0.5" sx={{ fontSize: 20 }} />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">
                Failed to load equipment data
              </p>
              <p className="text-sm text-red-600 mt-1">
                {equipmentErrorObj?.message || 'An unexpected error occurred.'}
              </p>
            </div>
            <Button variant="secondary" onClick={handleRefresh}>
              Retry
            </Button>
          </div>
        )}

        {/* Content */}
        <EquipmentAssetsTable
          equipment={equipment}
          categories={categories}
          isLoading={isLoading}
          onEdit={handleEdit}
          onCheckout={handleCheckout}
          onReturn={handleReturn}
          onMaintenance={handleMaintenance}
          onViewDetail={handleViewDetail}
          onViewTask={handleViewTask}
          onAddEquipment={handleAddEquipment}
        />

        {/* Form Modal (Create/Edit) */}
        <EquipmentAssetFormModal
          isOpen={isFormModalOpen}
          onClose={handleCloseFormModal}
          onSubmit={handleSubmitForm}
          equipment={selectedEquipment}
          categories={categories}
          warehouses={warehouses}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />

        {/* Checkout Modal */}
        <EquipmentCheckoutModal
          isOpen={isCheckoutModalOpen}
          onClose={handleCloseCheckoutModal}
          onCheckout={handleSubmitCheckout}
          equipment={equipmentToCheckout}
          isLoading={checkoutMutation.isPending}
        />

        {/* Return Modal */}
        <EquipmentReturnModal
          isOpen={isReturnModalOpen}
          onClose={handleCloseReturnModal}
          onReturn={handleSubmitReturn}
          equipment={equipmentToReturn}
          isLoading={returnMutation.isPending}
        />

        {/* Maintenance Modal */}
        <EquipmentMaintenanceModal
          isOpen={isMaintenanceModalOpen}
          onClose={handleCloseMaintenanceModal}
          onConfirm={handleSubmitMaintenance}
          equipment={equipmentToMaintenance}
          isLoading={maintenanceMutation.isPending}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={!!equipmentToDelete}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Delete Equipment"
          description={`Are you sure you want to delete "${equipmentToDelete?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          variant="danger"
        />

        {/* Task Modal - rendered via portal to avoid position offset from Layout's motion.div */}
        {createPortal(
          <CreateEditTaskModal
            isOpen={isTaskModalOpen}
            onClose={() => {
              setIsTaskModalOpen(false);
              setTask(null);
            }}
            task={task}
            projectId={task?.projectId}
            successTask={() => {
              setIsTaskModalOpen(false);
              setTask(null);
              refetchEquipment();
            }}
            initialTab="2"
            initialInventorySubTab="equipment"
          />,
          document.body
        )}
      </div>
    </WarehouseLayout>
  );
};

export default EquipmentAssetsPage;
